import {
  FlatJob,
  JobActionApproval,
  JobActionRegistration,
  JobCommentThread,
  JobDiagram,
  JobInput,
  JobNode,
  JobQuestion,
  JobStep
} from "@durable/durable-client";
import { useCallback, useState } from "react";

export type VisualTree = VisualBranch[];

/**
 * A single branch of data, containing its children and abstracted data for
 * display.
 */
export type VisualBranch = {
  // The node for the branch.
  node: JobNode;
  // The type of node represented by this branch.
  nodeType: string;
  // A list of child branches.
  children: VisualTree;
  // The total number of children (counted recursively) in this branch.
  totalChildren: number;
  // Is set to true if this branch or any of its children contain a question
  // node.
  hasQuestions: boolean;
};

/**
 * A utility struct with defaults for constructing VisualBranch structs without
 * having to specify all parameters.
 */
export const VisualBranchDefaults = {
  totalChildren: 0,
  hasQuestions: false
};

enum NodeTypePrompt {
  JOB_PROMPT = "jobPrompt"
}

/**
 * We add an explicit `JOB_PROMPT` entry to the usual `JobNode` types, to make
 * it clearer which is the initial input.
 *
 * This is a type expressing that, and can be used in `switch`'s `case`
 * statements to derive the correct subtype of `JobNode` (but these can't be
 * used as values -- there's some convoluted workarounds we have to do to
 * achieve that).
 */
export type TreeNodeType =
  | NodeTypePrompt
  | JobQuestion.node_type
  | JobStep.node_type
  | JobActionApproval.node_type
  | JobActionRegistration.node_type
  | JobInput.node_type
  | JobCommentThread.node_type
  | JobDiagram.node_type;

// The first of the contrived types for constructing the value.
//
// The basic point is that each of the `XNode.node_type`s are just compile-time
// symbols, so we can't use them as values (i.e. we can't do a bunch of
// `...XNode.node_type` spreads). But we'd still like to make the eventual
// `TreeNodeType` value be typed to match the type `TreeNodeType` exactly.
//
// So what we want is basically::
//
//     type ImplTreeNodeType = {
//       [NodeType in TreeNodeType as keyof typeof NodeType]:
//         `${NodeType[keyof NodeType]}`
//     }
//
//
// This doesn't work because `typeof NodeType` isn't valid, since `NodeType` is
// a type here (vs. e.g. `keyof typeof NodeTypePrompt` _is_ valid, since in that
// case `NodeTypePrompt` is the value, not the type).
//
// So we have to add this annoying union ourselves.
type TreeNodeTypeOfs =
  | typeof NodeTypePrompt
  | typeof JobQuestion.node_type
  | typeof JobStep.node_type
  | typeof JobActionApproval.node_type
  | typeof JobActionRegistration.node_type
  | typeof JobInput.node_type
  | typeof JobCommentThread.node_type
  | typeof JobDiagram.node_type;

// Typescript needs a little help to coerce each of these to a `string` that can
// be used with template literals.
type as_literal<T> = T extends string ? `${T}` : never;

// Now we can get our `ImplTreeNodeTypes`
type ImplTreeNodeType = {
  [NodeType in TreeNodeTypeOfs as keyof NodeType]: as_literal<
    NodeType[keyof NodeType]
  >;
};

// Now we can define our value, and it's typed correctly!
// eslint-disable-next-line no-redeclare
export const TreeNodeType: ImplTreeNodeType = {
  ...NodeTypePrompt,
  JOB_RESPONSE: "jobResponse",
  JOB_STEP: "jobStep",
  JOB_ACTION_APPROVAL: "jobActionApproval",
  JOB_ACTION_REGISTRATION: "jobActionRegistration",
  JOB_INPUT: "jobInput",
  JOB_COMMENT: "jobComment",
  JOB_DIAGRAM: "jobDiagram"
} as const;

// The default at which to truncate trees.
const MAX_DEPTH_DEFAULT = 3;

/**
 * A function which extracts a chain of singleton steps, meaning steps which
 * only have another step as children, and a remainder. The remainder contains
 * the children of the very last step in the chain of steps, in case these
 * children have questions. Otherwise, it's assumed that the steps themselves
 * will render their children.
 * @param branch The branch to extract the steps from. Must be a step itself.
 * @returns The array of extracted steps, plus the remainder.
 */
export function getSingletonSteps(
  branch: VisualBranch
): [VisualTree, VisualTree] {
  const stepArray: VisualTree = [];
  let branchIter = branch;
  let remainder = [branchIter];
  // Traverse the children of the branch.
  while (branchIter && branchIter.nodeType == TreeNodeType.JOB_STEP) {
    stepArray.push(branchIter);
    // If the branch is a step and only has one child, then add it to the
    // singleton step list and keep going.
    if (branchIter.children.length == 1) {
      branchIter = branchIter.children[0];
      remainder = [branchIter];
    } else {
      if (branchIter.hasQuestions) {
        // If the branch doesn't have 1 child and has questions under it, then
        // we have reached the end of the singletons and must display its
        // children separately.
        remainder = branchIter.children;
      } else {
        // If the branch has more than 1 child (or 0) and they are all steps,
        // then we can display them all directly underneath the step rather than
        // as part of the remainder.
        remainder = [];
      }
      break;
    }
  }
  return [stepArray, remainder];
}

/**
 * Given a node and a tree, recursively attempts to insert that node into
 * the tree.
 * @param node The node to insert.
 * @param tree The tree to insert it into. The node may not be inserted at the
 * top level, but rather somewhere down the hierarchy.
 * @returns True if the parent for the node was found and it was inserted. False
 * otherwise.
 */
export function insertIntoVisualTree(
  node: JobNode,
  tree: VisualTree
): boolean {
  let inserted: boolean = false;
  const isQuestion =
    node.data.node_type == (TreeNodeType.JOB_RESPONSE as string);
  // Go through each child of the tree.
  tree.map((subTree) => {
    // If that child's id matches the node's parent id, then the node must be
    // added to the child's children.
    if (subTree.node.id == node.parent_id) {
      subTree.children.push({
        node: node,
        nodeType: node.data.node_type,
        children: [],
        totalChildren: 0,
        hasQuestions: isQuestion
      });
      // Flag that we've found the parent and inserted a node.
      inserted = true;
      // Update the "totalChildren" array for the child, and in case the node
      // was a question, indicate that the child has questions under it.
      subTree.totalChildren += 1;
      if (isQuestion) {
        subTree.hasQuestions = true;
      }
    } else {
      // If that child's id didn't match the node's parent, recurse further down
      // the tree.
      const subtreeHasChildren = subTree.children.length > 0;
      if (subtreeHasChildren) {
        const subtreeInserted = insertIntoVisualTree(node, subTree.children);

        // If an insertion point was found under this child, updatea the
        // totalChildren and hasQuestion indicators.
        inserted ||= subtreeInserted;
        if (subtreeInserted) {
          subTree.totalChildren += 1;
          if (isQuestion) {
            subTree.hasQuestions = true;
          }
        }
      }
    }
  });

  return inserted;
}

/**
 * Trees from the appserver are sent as flat lists of nodes with parent
 * pointers. This function turns such a structure into a tree by adding nodes
 * under their parents.
 * @param flatJob The flattened job.
 * @returns A tree representation of the job.
 */
export function convertFlatJobtoVisualTree(flatJob: FlatJob): VisualTree {
  const visualTree: VisualTree = [];

  flatJob.flat_nodes.map((node) => {
    if (node.parent_id) {
      insertIntoVisualTree(node, visualTree);
    } else {
      visualTree.push({
        ...VisualBranchDefaults,
        node: node,
        nodeType: "jobPrompt",
        children: []
      });
    }
  });
  return visualTree;
}

/**
 * Given a branch, determines whether this branch can be detached from its
 * parent and visualized at the top-level.
 * @param branch The brach to analyze.
 * @param minBranchDepth The minimum depth that the branch must have before it
 * can be repositioned. Prevents branches of very small depths from being
 * repositioned to the top.
 * @returns True if the branch can be repositioned.
 */
export function shouldRepositionBranch(
  branch: VisualBranch,
  minBranchDepth: number = MAX_DEPTH_DEFAULT - 1
): boolean {
  return (
    branch.nodeType == TreeNodeType.JOB_STEP &&
    branch.totalChildren > minBranchDepth
  );
}

/**
 *
 * @param tree
 * @param maxDepth
 * @returns
 */
export function limitVisualTreeIndent(
  tree: VisualTree,
  maxDepth: number = MAX_DEPTH_DEFAULT
): VisualTree {
  /**
   *
   * @param topParent
   * @param parent
   * @param branch
   * @param depth
   */
  function nodeVisitor(
    topParent: VisualTree,
    parent: VisualTree,
    branch: VisualBranch,
    depth: number
  ) {
    const newNode: VisualBranch = {
      node: branch.node,
      nodeType: branch.nodeType,
      children: [],
      totalChildren: branch.totalChildren,
      hasQuestions: branch.hasQuestions
    };

    if (depth >= maxDepth && shouldRepositionBranch(branch)) {
      topParent.push(newNode);
      topParent = newNode.children;
      depth = 0;
      const newNodeWithoutChildren: VisualBranch = {
        node: branch.node,
        nodeType: branch.nodeType,
        children: [],
        totalChildren: branch.totalChildren,
        hasQuestions: branch.hasQuestions
      };
      parent.push(newNodeWithoutChildren);
    } else {
      parent.push(newNode);
    }
    const newDepth = branch.children.length > 1 ? depth + 1 : depth;
    branch.children.map((childNode) => {
      return nodeVisitor(topParent, newNode.children, childNode, newDepth);
    });
  }

  const newTree: VisualTree = [];
  tree.map((node) => {
    nodeVisitor(newTree, newTree, node, 0);
  });
  return newTree;
}

/**
 * Get a node's name.
 *
 * If the node's actual name is more than 15 characters,
 * truncate with ellipsis.
 */
function getNodeName(node: any) {
  const MaxNameLength = 15;
  const typeStr = node.type_node_str.node_str;
  return typeStr.length <= MaxNameLength
    ? typeStr
    : typeStr.substring(0, MaxNameLength - 3) + "...";
}

/**
 * Groups nodes by depth to prevent overwhelming the user with questions
 */
export function depthGroup(nodes: Array<any>) {
  const newNodes = [];
  let newDepthGroup = [];
  nodes.map((node, i) => {
    const collapse = node.needs_action && !(node.created_by == "user");
    if (collapse) {
      if (nodes[i + 1]?.depth == node.depth && nodes[i + 1].needs_action) {
        newDepthGroup.push(node);
      } else {
        newDepthGroup.push(node);
        newNodes.push(newDepthGroup);
        newDepthGroup = [];
      }
    } else {
      newNodes.push([node]);
    }
  });
  return newNodes;
}

/**
 * Build a node's json object from which to render the tree node.
 *
 * @param debugNode: Any debug node.
 * @param isRoot: Whether we're the root of the entire tree.
 * @returns A json object.
 */
function buildNodeJson(debugNode: any, isRoot: boolean = false) {
  const output: any = {};
  output.name = getNodeName(debugNode);
  output.properties = {};
  output.properties.is_root = isRoot;

  // Set the debug node on the graph element, but remove its children so we're
  // not recursively storing the same information.
  output.properties.debug_node = Object.assign({}, debugNode);
  output.properties.debug_node.children = [];

  // Recursively build the planner tree.
  output.children = [];
  debugNode.backward_nodes.forEach((bwdNode: any) => {
    output.children.push(buildNodeJson(bwdNode));
  });

  return output;
}

export const plannerAdapter = (data: any) => {
  const rootNode = data.root_node;
  const output: any = {};
  const hasRootNode = Object.keys(rootNode).length > 0;
  if (hasRootNode == false) {
    return output;
  }
  return [buildNodeJson(rootNode, true)];
};

export const useCenteredTree = (
  defaultTranslate = { x: 0, y: 0 }
): [{ x: number; y: number }, any] => {
  const [translate, setTranslate] = useState(defaultTranslate);
  const containerRef = useCallback((containerElem) => {
    if (containerElem !== null) {
      const { width, height } = containerElem.getBoundingClientRect();
      setTranslate({ x: width / 2, y: height / 2 });
    }
  }, []);
  return [translate, containerRef];
};

/**
 * Turn date into something human readable.
 */
export function getDateString(dateString: string) {
  const namedMonths = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December"
  ];
  const namedDays = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday"
  ];
  const date = new Date(dateString);
  return `${namedDays[date.getDay()]}, ${
    namedMonths[date.getMonth()]
  } ${date.getDate()}, ${date.getFullYear()}`;
}
/**
 * Sort the provided collection by the date at the object attribute.
 */
export function sortByDateAscending<Type>(
  collection: Type[],
  key: string = "created_at"
) {
  return collection.sort((sA, sB) => {
    const dateA = new Date(sA[key]);
    const dateB = new Date(sB[key]);
    if (dateA == dateB) return 0;
    if (dateA > dateB) return 1;
    return -1;
  });
}

export const getSecondsHumanReadable = (ms: number) => {
  const times: [string, number][] = [
    ["second", 1000],
    ["minute", 60],
    ["hour", 60],
    ["day", 24]
  ];
  let value = ms;
  let unit: string | null = null;
  const reversedTimes = times.reverse();
  let unitAndCount = reversedTimes.pop();
  while (unitAndCount && value > unitAndCount[1]) {
    value /= unitAndCount[1];
    unit = unitAndCount[0];
    unitAndCount = reversedTimes.pop();
  }
  const roundedValue = value.toFixed(0);

  return unit
    ? ` ${roundedValue} ${unit}${value > 1.5 ? "s" : ""} ago`
    : "just now";
};

export const getBytesHumanReadable = (bytes: number) => {
  const sizes = ["B", "KB", "MB", "GB", "TB"].reverse();
  let sizeStr = sizes.pop();
  while (bytes > 1024) {
    bytes /= 1024;
    sizeStr = sizes.pop();
  }

  return `${bytes.toFixed(1)} ${sizeStr}`;
};

/**
 * If the key is enter, submit.
 */
export function submitOnEnter(handler: (e) => void) {
  return (e) => {
    if (e.keyCode === 13 && handler != null) {
      handler(e);
    }
  };
}

/**
 * A shortcut way to handle onClick while maintaining accessibility.
 */
export function onClickwithKeyboard(
  handler: (e) => void,
  tabIndex: number = 0
) {
  return {
    role: "button",
    tabIndex: tabIndex,
    onKeyDown: submitOnEnter(handler),
    onClick: handler
  };
}

/**
 * Quickly format strings with underscores or improper capitalization.
 */
export function capitalizeAndReplaceUnderscores(inputString: string): string {
  if (!inputString) {
    return "";
  }

  // Replace underscores with spaces
  const stringWithSpaces = inputString.replace(/_/g, " ");

  // Capitalize the first letter
  return stringWithSpaces.charAt(0).toUpperCase() + stringWithSpaces.slice(1);
}

/**
 * Trim the length of an input string.
 */
export function trimStringLength(
  inputString: string,
  maxLength: number = 300
): string {
  if (inputString.length <= maxLength) {
    return inputString;
  } else {
    return inputString.slice(0, maxLength - 3) + "...";
  }
}
